import { useState } from "react"
import { api } from "../../../../../env"
import { errMessage } from "../../../../../utils/errors/errorHandler"
import LoadingButton from "../../../../../components/LoadingButton"
import { AreaField, TextField } from "../fields"

const RUBRIC_LABELS = ["Emerging", "Developing", "Proficient", "Advanced"]
const emptyCapability = () => ({ name: "", levels: ["", "", "", ""] })

export default function TrackDNA({ value, onChange, blocks, onBack, onNext }) {

    const [loading, setLoading] = useState(false)
    const [error, setError] = useState("")
    const set = (field, v) => { setError(""); onChange({ ...value, [field]: v }) }

    const setCapability = (idx, capability) => {
        const capabilities = [...(value.capabilities ?? [])]
        capabilities[idx] = capability
        set("capabilities", capabilities)
    }
    const setCapabilityLevel = (idx, levelIdx, v) => {
        const cap = { ...(value.capabilities[idx]) }
        const levels = [...(cap.levels ?? ["", "", "", ""])]
        levels[levelIdx] = v
        setCapability(idx, { ...cap, levels })
    }
    const addCapability = () => set("capabilities", [...(value.capabilities ?? []), emptyCapability()])
    const removeCapability = (idx) => set("capabilities", value.capabilities.filter((_, i) => i !== idx))

    const autoFill = () => {
        setError("")
        setLoading(true)
        api.post("tracks/generate/dna", blocks)
            .then(res => {
                const d = res.data ?? {}
                onChange({
                    coreBelief: d.coreBelief ?? "",
                    capabilities: Array.isArray(d.capabilities) && d.capabilities.length
                        ? d.capabilities.map(c => ({ name: c?.name ?? "", levels: (c?.levels ?? []).slice(0, 4).concat(["", "", "", ""]).slice(0, 4) }))
                        : [emptyCapability()],
                    currentIdentity: d.currentIdentity ?? "",
                    futureIdentity: d.futureIdentity ?? "",
                    transformationStatement: d.transformationStatement ?? "",
                })
                setLoading(false)
            })
            .catch(e => {
                setError(errMessage(e, "Couldn't generate Track DNA. Please try again."))
                setLoading(false)
            })
    }

    const capabilities = value.capabilities ?? []

    return (
        <div className="card standard-padding flex columns gap-s showing">
            <div className="flex columns gap-xxs">
                <p className="card-title">Track DNA</p>
                <p className="card-body neutral">Define what makes this track distinct. Let the AI draft it from your answers, then edit anything.</p>
            </div>

            <div className="bt-ai-banner">
                <span className="material-symbols-outlined bt-ai-banner-icon">auto_awesome</span>
                <div className="flex columns gap-xxs bt-ai-banner-copy">
                    <p className="card-body medium">Draft with AI</p>
                    <p className="card-label neutral">Generate a core belief, capabilities, identity shift and outcome statement from your questionnaire.</p>
                </div>
                <LoadingButton className="solid" label="Auto-fill with AI" leadingIcon="auto_awesome" loading={loading} onClick={autoFill} />
            </div>

            {error && <p className="card-label error showing">{error}</p>}

            <AreaField label="Core belief" value={value.coreBelief} onChange={v => set("coreBelief", v)} placeholder="The core belief participants internalise" minRows={2} maxRows={4} />

            <div className="flex space-between center gap-xs vi-spacer-xs">
                <p className="card-title">Core capabilities</p>
                <button className="text" onClick={addCapability}>+ Add capability</button>
            </div>
            {capabilities.length === 0 && <p className="card-body neutral">No capabilities yet — add one or use Auto-fill.</p>}
            {capabilities.map((cap, idx) => (
                <div key={idx} className="bt-capability">
                    <div className="flex space-between center gap-xs">
                        <span className="bt-step-num">{idx + 1}</span>
                        <input className="bt-capability-name" value={cap.name ?? ""} placeholder="Capability name (e.g. Influence)" onChange={e => setCapability(idx, { ...cap, name: e.target.value })} />
                        <button className="icon outline small" onClick={() => removeCapability(idx)}><span className="material-symbols-outlined scale-xs">close</span></button>
                    </div>
                    <div className="bt-rubric-grid">
                        {RUBRIC_LABELS.map((rl, li) => (
                            <div key={li} className="bt-rubric-cell">
                                <p className={`bt-rubric-label bt-rubric-label--${li}`}>{rl}</p>
                                <div className="input-wrapper regular-border">
                                    <input value={cap.levels?.[li] ?? ""} placeholder="What does this level look like?" onChange={e => setCapabilityLevel(idx, li, e.target.value)} />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ))}

            <div className="grid gap-s vi-spacer-xs" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))" }}>
                <TextField label="Current identity" value={value.currentIdentity} onChange={v => set("currentIdentity", v)} placeholder="e.g. Capable manager" />
                <TextField label="Future identity" value={value.futureIdentity} onChange={v => set("futureIdentity", v)} placeholder="e.g. Trusted leader" />
            </div>
            <AreaField label="Transformation statement" value={value.transformationStatement} onChange={v => set("transformationStatement", v)} placeholder="By the end of this track, participants will..." minRows={2} maxRows={4} />

            <hr className="vi-spacer-xs" />
            <div className="flex space-between center gap-xs bt-footer">
                <button className="outline with-icon leading" onClick={onBack}>
                    <span className="material-symbols-outlined scale-xs">chevron_left</span>
                    <span>Back</span>
                </button>
                <button className="solid with-icon trailing" onClick={onNext}>
                    <span>Continue to Chapters</span>
                    <span className="material-symbols-outlined scale-xs">chevron_right</span>
                </button>
            </div>
        </div>
    )
}
